import { useState, useEffect, useRef, useCallback } from 'react';
/**
 * Custom hook for observing element intersection with the viewport
 * @param options - Intersection Observer options
 * @returns Tuple of ref setter, inView status, and intersection entry
 */
export function useIntersectionObserver(options) {
    if (options === void 0) { options = {}; }
    var _a = options.once, once = _a === void 0 ? false : _a, _b = options.threshold, threshold = _b === void 0 ? 0 : _b, _c = options.root, root = _c === void 0 ? null : _c, _d = options.rootMargin, rootMargin = _d === void 0 ? '0px' : _d;
    var _e = useState(false), inView = _e[0], setInView = _e[1];
    var _f = useState(null), entry = _f[0], setEntry = _f[1];
    var _g = useState(null), node = _g[0], setNode = _g[1];
    var handleIntersect = useCallback(function (entries) {
        var _a;
        var observedEntry = entries[0];
        setInView(observedEntry.isIntersecting);
        setEntry(observedEntry);
        // Stop observing if 'once' option is true and element is intersecting
        if (once && observedEntry.isIntersecting) {
            (_a = observer.current) === null || _a === void 0 ? void 0 : _a.disconnect();
        }
    }, [once]);
    var observer = useRef(null);
    // Ref callback to set the node
    var ref = useCallback(function (node) {
        setNode(node);
    }, []);
    useEffect(function () {
        // Only create observer if a node is attached
        if (!node)
            return;
        // Disconnect previous observer if it exists
        if (observer.current) {
            observer.current.disconnect();
        }
        // Create new Intersection Observer
        observer.current = new IntersectionObserver(handleIntersect, {
            root: root,
            rootMargin: rootMargin,
            threshold: threshold,
        });
        // Start observing the element
        observer.current.observe(node);
        // Cleanup function to disconnect observer
        return function () {
            if (observer.current) {
                observer.current.disconnect();
            }
        };
    }, [node, handleIntersect, root, rootMargin, threshold]);
    return [ref, inView, entry];
}
